import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { observer } from 'mobx-react-lite';
import Separator from 'shared/layout/Separator';
import Spacer from 'shared/layout/Spacer';
import styled from 'styled-components';

import { ARTICLE_MAX_WIDTH } from '../styles/constants';

import { Button } from '@components/button';
import { useMedia } from '@hooks/use-media';

import useDetailLoad from 'hooks/useDetailLoad';
import useDocumentTitle from 'hooks/useDocumentTitle';
import usePreviewCheck from 'hooks/usePreviewCheck';
import useScrollToTop from 'hooks/useScrollToTop';
import { useStore } from 'models/stores';

import BackButton from './BackButton';
import JobHeader, { HeaderGroup } from './JobHeader';
import LanguageDataNotAvailable from './LanguageDataNotAvailable';
import MainHeader from './MainHeader';

function JobDetail() {
  const { jobStore, uiStore, companyStore } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { jobID } = useParams();
  const small = useMedia(`(max-width: 600px)`);

  uiStore.selectJob(jobID);
  const job = jobStore.items.get(jobID);
  const data = job && job.languages.find((l) => l.language === uiStore.language);
  useDocumentTitle(data && data.title);
  const company = companyStore.current.name;
  usePreviewCheck(jobID);

  useDetailLoad(jobID, jobStore, uiStore, company, () => {
    navigate(`/${company}/${uiStore.language}/notfound/`);
  });

  useScrollToTop();

  if (!job || job.state !== 'loaded') {
    return null;
  }

  const applyButton = (
    <Link to={{ pathname: 'apply', search: location.search }}>
      <Button text={uiStore.t('apply_button')} isFullWidth={small} />
    </Link>
  );
  return (
    <>
      <MainHeader>
        {uiStore.preview ? (
          <div />
        ) : (
          <BackButton
            to={{
              pathname: `/${company}/${uiStore.language}`,
              search: location.search,
            }}
            state={location.state}
            text={uiStore.t('backToJobs')}
          />
        )}
        {data && applyButton}
      </MainHeader>
      {data ? (
        <Wrapper>
          <HeaderGroup>
            <JobHeader job={job} langData={data} showDeadline />
          </HeaderGroup>
          <Spacer top={32} bottom={32}>
            <Separator />
          </Spacer>
          <Spacer top={30} bottom={30}>
            <Html dangerouslySetInnerHTML={{ __html: data.description }} />
          </Spacer>
          <MobileApply>{applyButton}</MobileApply>
        </Wrapper>
      ) : (
        <LanguageDataNotAvailable />
      )}
    </>
  );
}
export default observer(JobDetail);

const MobileApply = styled.div`
  display: none;
  @media (max-width: ${(p) => p.theme.bp.sm}) {
    margin-top: 48px;
    display: block;
    width: 100%;
  }
`;

const Wrapper = styled.div`
  margin: 0 auto;
  width: 100%;
  padding: 40px 0;
  max-width: ${ARTICLE_MAX_WIDTH};
  @media (max-width: 720px) {
    padding: 0 40px 40px;
  }

  @media (max-width: 460px) {
    padding: 0 24px 40px;
  }
`;

const Html = styled.div`
  color: ${(props) => props.theme.textColor};
  font-size: 16px;
  font-weight: normal;
  font-family: ${(props) => props.theme.fontFamily};
  line-height: 24px;
  p {
    margin: 0;
    padding: 0;
  }
  p:empty {
    padding: 11px 0;
  }
  ol,
  ul {
    margin: 4px;
  }
  > p > a {
    color: ${(props) => props.theme.linkColor || props.theme.primaryColor};
    &:hover {
      text-decoration: underline;
    }
  }
`;
